/* eslint-disable @typescript-eslint/no-explicit-any */
import { Card, CardContent } from "@/components/ui/card";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import {
	Pagination,
	PaginationContent,
	PaginationEllipsis,
	PaginationItem,
	PaginationLink,
	PaginationNext,
	PaginationPrevious,
} from "@/components/ui/pagination";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Button, DateRangePicker, DateRangePickerItem, Select as TremorSelect, SelectItem as TremorSelectItem, TextInput } from "@tremor/react";
import { el } from "date-fns/locale";
import { ChevronDownIcon, ChevronUpIcon, FilterIcon, SearchIcon, XCircleIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type React from "react";
import { useMemo, useState, useEffect } from "react";
import { DateTime } from "luxon";

export interface ColumnFilter {
	type: "date-range" | "select" | "boolean" | "number-range";
	trueLabel?: string;
	falseLabel?: string;
	options?: string[];
	filterValue?: (row: any) => any;
}

export interface Column {
	key: string;
	header: string;
	tooltip?: string;
	filter?: ColumnFilter;
	render?: (value: any, row: any) => React.ReactNode;
	sortable?: boolean;
	sortValue?: (row: any) => any;
	searchFunction?: (value: any, searchTerm: string) => boolean;
}

interface ReusableTableProps<T = any> {
	data: T[];
	columns: Column[];
	onRowClick?: (row: T) => void;
	searchableFields?: string[];
	onDateRangeChange?: (range: { from?: Date; to?: Date }) => void;
	period?: string;
	defaultSort?: { key: string; direction: "asc" | "desc" };
	searchFunction?: (filters: { from?: string; to?: string; start?: number; limit?: number; sort?: string; show_refunded?: string; search?: string; kind_action?: string; kind_type?: string; is_manual?: string }) => void;
	// Pagination props
	enablePagination?: boolean;
	totalItems?: number;
	itemsPerPageOptions?: number[];
	defaultItemsPerPage?: number;
	// Custom filters
	showRefundedFilter?: boolean;
	defaultRefundedFilter?: string;
	showKindFilters?: boolean;
	showManualFilter?: boolean;
	dateRangeFilter?: boolean;
}

const ReusableTable: React.FC<ReusableTableProps> = ({
	data,
	columns,
	onRowClick,
	searchableFields = [],
	period,
	defaultSort,
	searchFunction,
	enablePagination = false,
	totalItems = 0,
	itemsPerPageOptions = [50, 100, 200],
	defaultItemsPerPage = 50,
	showRefundedFilter = false,
	defaultRefundedFilter = "not_refunded",
	showKindFilters = false,
	showManualFilter = false,
	dateRangeFilter = false,
}) => {
	const [sortConfig, setSortConfig] = useState<Array<{
		key: string;
		colIndex: number;
		direction: "asc" | "desc";
	}>>(() => {
		if (!defaultSort) return [];
		const colIndex = columns.findIndex((column) => column.key === defaultSort.key);
		return colIndex === -1 ? [] : [{ ...defaultSort, colIndex }];
	});
	const [searchTerm, setSearchTerm] = useState("");
	const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
	const [currentPage, setCurrentPage] = useState(1);
	const [itemsPerPage, setItemsPerPage] = useState(defaultItemsPerPage);
	const last30DaysFilter = {
		from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
		to: new Date().toISOString(),
		selectValue: "Τελευταίες 30 ημέρες",
	};
	const [dateFilters, setDateFilters] = useState<{ from?: string; to?: string; selectValue?: string }>(() => {
		if (!dateRangeFilter) return {};
		if (period === "Σήμερα" || period === "Τελευταίες 7 ημέρες") {
			// Match the date presets using Athens day boundaries, converted to UTC.
			const athensNow = DateTime.now().setZone("Europe/Athens");
			const startDate = period === "Τελευταίες 7 ημέρες"
				? athensNow.minus({ days: 7 })
				: athensNow;
			return {
				from: startDate.startOf("day").toUTC().toJSDate().toISOString(),
				to: athensNow.endOf("day").toUTC().toJSDate().toISOString(),
			};
		}
		return dateRangeFilter && !period ? last30DaysFilter : {};
	});
	const [refundedFilter, setRefundedFilter] = useState<string>(defaultRefundedFilter);
	const [kindAction, setKindAction] = useState<string>("all");
	const [kindType, setKindType] = useState<string>("all");
	const [manualFilter, setManualFilter] = useState<string>("all");
	const [columnFilters, setColumnFilters] = useState<Record<number, any>>({});

	// Debounce search term
	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearchTerm(searchTerm);
			if (enablePagination) {
				setCurrentPage(1); // Reset to first page when search changes
			}
		}, 300);

		return () => clearTimeout(timer);
	}, [searchTerm, enablePagination]);

	const requestSort = (key: string, colIndex: number, isCtrl: boolean) => {
		setSortConfig((prev) => {
			if (isCtrl) {
				// Ctrl+click manages secondary sort
				if (prev.length === 0) return prev; // no primary yet, ignore
				const secIdx = prev.findIndex((s, i) => i === 1 && s.colIndex === colIndex);
				if (secIdx === 1) {
					if (prev[1].direction === "asc") {
						// asc → desc
						const updated = [...prev];
						updated[1] = { ...updated[1], direction: "desc" };
						return updated;
					} else {
						// desc → remove secondary
						return [prev[0]];
					}
				}
				// Add as secondary (asc), ignore if same as primary
				if (prev[0].colIndex === colIndex) return prev;
				return [prev[0], { key, colIndex, direction: "asc" as const }];
			} else {
				// Plain click manages primary sort
				if (prev.length > 0 && prev[0].colIndex === colIndex) {
					if (prev[0].direction === "asc") {
						// asc → desc
						const updated = [...prev];
						updated[0] = { ...updated[0], direction: "desc" };
						return updated;
					} else {
						// desc → remove all sorting
						return [];
					}
				}
				// New primary, reset secondary
				return [{ key, colIndex, direction: "asc" as const }];
			}
		});
		setCurrentPage(1);
	};

	const sortedData = useMemo(() => {
		if (!Array.isArray(data)) return [];

		// If pagination is enabled, sorting is handled server-side, so just return data
		if (enablePagination) {
			return data;
		}

		// Client-side sorting when pagination is disabled
		const sortableItems = [...data];
		if (sortConfig.length > 0) {
			sortableItems.sort((a, b) => {
				for (const sc of sortConfig) {
					const col = columns[sc.colIndex];
					const aValue = col?.sortValue ? col.sortValue(a) : a[sc.key];
					const bValue = col?.sortValue ? col.sortValue(b) : b[sc.key];

					let result = 0;

					if (aValue == null && bValue == null) { result = 0; }
					else if (aValue == null) { result = sc.direction === "asc" ? -1 : 1; }
					else if (bValue == null) { result = sc.direction === "asc" ? 1 : -1; }
					else if (aValue instanceof Date && bValue instanceof Date) {
						result = sc.direction === "asc"
							? aValue.getTime() - bValue.getTime()
							: bValue.getTime() - aValue.getTime();
					} else {
						const aNum = Number(aValue);
						const bNum = Number(bValue);
						if (!isNaN(aNum) && !isNaN(bNum)) {
							result = sc.direction === "asc" ? aNum - bNum : bNum - aNum;
						} else if (typeof aValue === "string" && typeof bValue === "string") {
							result = sc.direction === "asc"
								? aValue.localeCompare(bValue)
								: bValue.localeCompare(aValue);
						} else {
							result = aValue < bValue
								? (sc.direction === "asc" ? -1 : 1)
								: aValue > bValue ? (sc.direction === "asc" ? 1 : -1) : 0;
						}
					}

					if (result !== 0) return result;
				}
				return 0;
			});
		}
		return sortableItems;
	}, [data, sortConfig, enablePagination]);

	const filteredData = useMemo(() => {
		// If pagination is enabled, filtering is handled server-side
		if (enablePagination) {
			return sortedData;
		}

		let result = sortedData;

		// Apply search term filter
		if (searchTerm !== "") {
			result = result.filter((row) => {
				const fieldsToSearch =
					searchableFields.length > 0 ? searchableFields : Object.keys(row);
				return fieldsToSearch.some((field) => {
					const column = columns.find((col) => col.key === field);
					if (column?.searchFunction) {
						return column.searchFunction(row[field], searchTerm);
					}
					return String(row[field])
						.toLowerCase()
						?.includes(searchTerm.toLowerCase());
				});
			});
		}

		// Apply column filters
		columns.forEach((column, index) => {
			const filterVal = columnFilters[index];
			if (!filterVal || !column.filter) return;

			result = result.filter((row) => {
				switch (column.filter!.type) {
					case "date-range": {
						const rawVal = row[column.key];
						if (!rawVal) return true;
						const val = new Date(rawVal).getTime();
						const from = filterVal.from ? new Date(filterVal.from).getTime() : null;
						const to = filterVal.to ? new Date(filterVal.to).getTime() : null;
						if (from && val < from) return false;
						if (to && val > to) return false;
						return true;
					}
					case "select":
						if (!filterVal || filterVal === "all") return true;
						return row[column.key] === filterVal;
					case "boolean": {
						if (filterVal === "all") return true;
						const boolVal = column.filter!.filterValue ? column.filter!.filterValue(row) : row[column.key];
						return boolVal == (filterVal === "true" ? 1 : 0);
					}
					case "number-range": {
						const val = column.filter!.filterValue
							? column.filter!.filterValue(row)
							: row[column.key];
						if (filterVal.min != null && !isNaN(filterVal.min) && val < filterVal.min) return false;
						if (filterVal.max != null && !isNaN(filterVal.max) && val > filterVal.max) return false;
						return true;
					}
					default:
						return true;
				}
			});
		});

		return result;
	}, [sortedData, searchTerm, searchableFields, columns, columnFilters, enablePagination]);

	// Trigger search function when pagination, sorting, or filters change
	useEffect(() => {
		if (enablePagination && searchFunction) {
			const start = (currentPage - 1) * itemsPerPage;
			const filters: any = {
				...dateFilters,
				start,
				limit: itemsPerPage
			};

			// Add sorting parameters if sorting is configured
			if (sortConfig.length > 0) {
				filters.sort = sortConfig.map((s) => `${s.direction === "desc" ? "-" : "+"}${s.key}`).join(",");
			}

			// Add refunded filter
			if (showRefundedFilter && refundedFilter !== "all") {
				filters.show_refunded = refundedFilter;
			}

			// Add search term
			if (debouncedSearchTerm && debouncedSearchTerm.trim() !== "") {
				filters.search = debouncedSearchTerm.trim();
			}

			// Add kind filters
			if (showKindFilters) {
				if (kindAction !== "all") {
					filters.kind_action = kindAction;
				}
				if (kindType !== "all") {
					filters.kind_type = kindType;
				}
			}

			// Add manual filter
			if (showManualFilter && manualFilter !== "all") {
				filters.is_manual = manualFilter;
			}

			searchFunction(filters);
		}
	}, [currentPage, itemsPerPage, sortConfig, refundedFilter, debouncedSearchTerm, kindAction, kindType, manualFilter, dateFilters]);

	const handleDateRangeChange = (range: { from?: Date; to?: Date }) => {
		const { from, to } = range;

		if (!from && !to) {
			setDateFilters(last30DaysFilter);
			setCurrentPage(1);
			// When paginated, the fetch effect reacts to the dateFilters change
			if (!enablePagination && searchFunction) {
				searchFunction({ from: last30DaysFilter.from, to: last30DaysFilter.to });
			}
			return;
		}

		// Convert Athens timezone date selections to UTC for database query
		const convertAthensDateToUTC = (date: Date, isEndOfDay: boolean) => {
			// Parse date and set time in Athens timezone
			const athensDateTime = DateTime.fromJSDate(date, { zone: 'Europe/Athens' })
				.set({
					hour: isEndOfDay ? 23 : 0,
					minute: isEndOfDay ? 59 : 0,
					second: isEndOfDay ? 59 : 0,
					millisecond: isEndOfDay ? 999 : 0
				});

			// Convert to UTC and return as JS Date
			return athensDateTime.toUTC().toJSDate();
		};

		// If from is provided, always set toDate (either explicit to, or same as from for single day)
		const fromDate = from ? convertAthensDateToUTC(from, false) : undefined;
		const toDate = from ? convertAthensDateToUTC(to || from, true) : undefined;

		const filters: any = {
			from: fromDate?.toISOString(),
			to: toDate?.toISOString()
		};
		setDateFilters(filters);
		setCurrentPage(1); // Reset to first page when date changes

		// When paginated, the fetch effect reacts to the dateFilters change
		if (!enablePagination && searchFunction) {
			searchFunction(filters);
		}
	};

	const handleItemsPerPageChange = (value: string) => {
		setItemsPerPage(Number(value));
		setCurrentPage(1); // Reset to first page when items per page changes
	};

	// Calculate total pages
	const totalPages = enablePagination ? Math.ceil(totalItems / itemsPerPage) : 0;

	// Generate page numbers to display
	const getPageNumbers = () => {
		const pages: (number | string)[] = [];
		const maxPagesToShow = 5;

		if (totalPages <= maxPagesToShow) {
			for (let i = 1; i <= totalPages; i++) {
				pages.push(i);
			}
		} else {
			if (currentPage <= 3) {
				for (let i = 1; i <= 4; i++) {
					pages.push(i);
				}
				pages.push("ellipsis");
				pages.push(totalPages);
			} else if (currentPage >= totalPages - 2) {
				pages.push(1);
				pages.push("ellipsis");
				for (let i = totalPages - 3; i <= totalPages; i++) {
					pages.push(i);
				}
			} else {
				pages.push(1);
				pages.push("ellipsis");
				pages.push(currentPage - 1);
				pages.push(currentPage);
				pages.push(currentPage + 1);
				pages.push("ellipsis");
				pages.push(totalPages);
			}
		}

		return pages;
	};

	const isFilterActive = (index: number) => {
		const filterVal = columnFilters[index];
		if (!filterVal) return false;
		const col = columns[index];
		switch (col.filter?.type) {
			case "date-range": return !!(filterVal.from || filterVal.to);
			case "select":
			case "boolean": return filterVal !== "all";
			case "number-range": return filterVal.min != null || filterVal.max != null;
			default: return false;
		}
	};

	const renderFilterContent = (column: Column, index: number) => {
		const filter = column.filter!;
		const filterVal = columnFilters[index] || {};

		const setFilter = (val: any) =>
			setColumnFilters((prev) => ({ ...prev, [index]: val }));

		const clearFilter = () =>
			setColumnFilters((prev) => {
				const next = { ...prev };
				delete next[index];
				return next;
			});

		let content: React.ReactNode = null;

		switch (filter.type) {
			case "date-range":
				content = (
					<DateRangePicker
						placeholder="Επιλέξτε περίοδο"
						selectPlaceholder="Προεπιλογές"
						locale={el}
						enableClear
						value={filterVal.from || filterVal.to ? {
							from: filterVal.from ? new Date(filterVal.from) : undefined,
							to: filterVal.to ? new Date(filterVal.to) : undefined,
						} : undefined}
						onValueChange={(range) => {
							if (!range.from && !range.to) { clearFilter(); return; }
							setFilter({ from: range.from?.toISOString(), to: range.to?.toISOString() });
						}}
					>
						<DateRangePickerItem
							key="today" value="Σήμερα"
							from={new Date()} to={new Date()} />
						<DateRangePickerItem
							key="7days" value="7 ημέρες"
							from={new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)}
							to={new Date()} />
						<DateRangePickerItem
							key="30days" value="30 ημέρες"
							from={new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)}
							to={new Date()} />
						<DateRangePickerItem
							key="monthToDate"
							value="Από αρχή του μήνα έως σήμερα"
							to={new Date()}
							from={new Date(new Date().getFullYear(), new Date().getMonth(), 1)}
						/>
						<DateRangePickerItem
							key="yearToDate"
							value="Από αρχή του χρόνου έως σήμερα"
							to={new Date()}
							from={new Date(new Date().getFullYear(), 0, 1)}
						/>
					</DateRangePicker>
				);
				break;
			case "select": {
				const options = filter.options || [...new Set(data.map((row) => row[column.key]).filter(Boolean))] as string[];
				const selectVal = columnFilters[index] || "all";
				content = (
					<TremorSelect value={selectVal} onValueChange={(val) => val === "all" ? clearFilter() : setFilter(val)}>
						<TremorSelectItem value="all">Όλα</TremorSelectItem>
						{options.map((opt) => <TremorSelectItem key={opt} value={opt}>{opt}</TremorSelectItem>)}
					</TremorSelect>
				);
				break;
			}
			case "boolean": {
				const boolVal = columnFilters[index] || "all";
				content = (
					<TremorSelect value={boolVal} onValueChange={(val) => val === "all" ? clearFilter() : setFilter(val)}>
						<TremorSelectItem value="all">Όλοι</TremorSelectItem>
						<TremorSelectItem value="true">{filter.trueLabel ?? "Ναι"}</TremorSelectItem>
						<TremorSelectItem value="false">{filter.falseLabel ?? "Όχι"}</TremorSelectItem>
					</TremorSelect>
				);
				break;
			}
			case "number-range":
				content = (
					<div className="space-y-2">
						<div className="flex items-center gap-2">
							<TextInput
								type="number"
								placeholder="Min"
								min={0}
								max={100}
								value={filterVal.min ?? ""}
								onChange={(e) => setFilter({ ...filterVal, min: e.target.value !== "" ? Number(e.target.value) : null })}
							/>
							<span className="text-sm">–</span>
							<TextInput
								type="number"
								placeholder="Max"
								min={0}
								max={100}
								value={filterVal.max ?? ""}
								onChange={(e) => setFilter({ ...filterVal, max: e.target.value !== "" ? Number(e.target.value) : null })}
							/>
						</div>
						{(filterVal.min != null || filterVal.max != null) && (
							<Button color="red" onClick={clearFilter}>
								Καθαρισμός φίλτρου
							</Button>
						)}
					</div>
				);
				break;
		}

		return (
			<div className="space-y-3">
				<p className="text-sm font-medium text-muted-foreground">{column.header}</p>
				{content}
			</div>
		);
	};

	return (
		<Card>
			<CardContent className="pt-2 pl-2">
				<div className="w-full mb-5 mt-5 gap-4">
					<div className="flex flex-row items-center gap-4 ">
						<TextInput
							placeholder="Αναζήτηση..."
							value={searchTerm}
							onValueChange={setSearchTerm}
							className="max-w-sm"
							icon={SearchIcon}
						/>
						{Object.keys(columnFilters).length > 0 && (
							<Button
								className="flex items-center gap-1 text-sm"
								onClick={() => setColumnFilters({})}
								icon={XCircleIcon}
								color={'red'}
							>
								Καθαρισμός φίλτρων
							</Button>
						)}
						{showRefundedFilter && (
							<Select
								value={refundedFilter}
								onValueChange={(value) => {
									setRefundedFilter(value);
									setCurrentPage(1);
								}}
							>
								<SelectTrigger className="w-[180px]">
									<SelectValue placeholder="Αποζημίωση" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="all">Όλα</SelectItem>
									<SelectItem value="refunded">Αποζημιωμένα</SelectItem>
									<SelectItem value="not_refunded">Μη αποζημιωμένα</SelectItem>
								</SelectContent>
							</Select>
						)}
						{showKindFilters && (
							<>
								<Select
									value={kindAction}
									onValueChange={(value) => {
										setKindAction(value);
										setCurrentPage(1);
									}}
								>
									<SelectTrigger className="w-[180px]">
										<SelectValue placeholder="Ενέργεια" />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="all">Όλες οι ενέργειες</SelectItem>
										<SelectItem value="insert">Εισαγωγή</SelectItem>
										<SelectItem value="recheck">Επανέλεγχος</SelectItem>
									</SelectContent>
								</Select>
								<Select
									value={kindType}
									onValueChange={(value) => {
										setKindType(value);
										setCurrentPage(1);
									}}
								>
									<SelectTrigger className="w-[150px]">
										<SelectValue placeholder="Τύπος" />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="all">Όλοι οι τύποι</SelectItem>
										<SelectItem value="afm">ΑΦΜ</SelectItem>
										<SelectItem value="kaek">ΚΑΕΚ</SelectItem>
									</SelectContent>
								</Select>
							</>
						)}
						{showManualFilter && (
							<Select
								value={manualFilter}
								onValueChange={(value) => {
									setManualFilter(value);
									setCurrentPage(1);
								}}
							>
								<SelectTrigger className="w-[150px]">
									<SelectValue placeholder="Τρόπος" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="all">Όλα</SelectItem>
									<SelectItem value="manual">Χειροκίνητο</SelectItem>
									<SelectItem value="automatic">Αυτόματο</SelectItem>
								</SelectContent>
							</Select>
						)}
					</div>
					{dateRangeFilter &&
						<div className="pr-10 min-w-1 mt-2">
							<DateRangePicker
								placeholder="Επιλέξτε περίοδο"
								selectPlaceholder="Προεπιλεγμένα Διαστήματα"
								locale={el}
								onValueChange={handleDateRangeChange}
								defaultValue={{ selectValue: period }}
								enableClear={true}
								value={dateFilters.from || dateFilters.to ? {
									from: dateFilters.from ? new Date(dateFilters.from) : undefined,
									to: dateFilters.to ? new Date(dateFilters.to) : undefined,
									selectValue: dateFilters.selectValue,
								} : undefined}
							>
								<DateRangePickerItem
									key="today"
									value="Σήμερα"
									to={new Date()}
									from={new Date()}
								/>
								<DateRangePickerItem
									key="7days"
									value="Τελευταίες 7 ημέρες"
									to={new Date()}
									from={new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)}
								/>
								<DateRangePickerItem
									key="30days"
									value="Τελευταίες 30 ημέρες"
									to={new Date()}
									from={new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)}
								/>
								<DateRangePickerItem
									key="monthToDate"
									value="Από αρχή του μήνα έως σήμερα"
									to={new Date()}
									from={
										new Date(new Date().getFullYear(), new Date().getMonth(), 1)
									}
								/>
								<DateRangePickerItem
									key="yearToDate"
									value="Από αρχή του χρόνου έως σήμερα"
									to={new Date()}
									from={new Date(new Date().getFullYear(), 0, 1)}
								/>
							</DateRangePicker>
						</div>
					}
				</div>

				<Table>
					<TableHeader>
						<TableRow>
							{columns?.map((column, index) => (
								<TableHead
									key={`header-${index}`}
									className="cursor-pointer whitespace-normal break-words"
									onClick={(e) =>
										column.sortable !== false && requestSort(column.key, index, e.ctrlKey)
									}
								>
									<div className="flex items-center gap-1" title={column.tooltip}>
										{column.header}
										{column.sortable !== false && (() => {
											const scIdx = sortConfig.findIndex((s) => s.colIndex === index);
											if (scIdx === -1) return <ChevronUpIcon className="h-4 w-4 ml-1 opacity-0" />;
											const sc = sortConfig[scIdx];
											return (
												<span className="ml-1 flex items-center gap-0.5">
													{sc.direction === "asc"
														? <ChevronUpIcon className="h-4 w-4" />
														: <ChevronDownIcon className="h-4 w-4" />}
													{sortConfig.length > 1 && (
														<span className="text-[10px] font-bold leading-none">{scIdx + 1}</span>
													)}
												</span>
											);
										})()}
										{column.filter && (
											<Popover>
												<PopoverTrigger asChild onClick={(e) => e.stopPropagation()}>
													<span className="cursor-pointer">
														<FilterIcon className={`h-4 w-4 ${isFilterActive(index) ? "text-blue-500 fill-blue-500" : "text-gray-400 hover:text-gray-600"}`} />
													</span>
												</PopoverTrigger>
												<PopoverContent className="w-[420px] p-4" onClick={(e) => e.stopPropagation()}>
													{renderFilterContent(column, index)}
												</PopoverContent>
											</Popover>
										)}
									</div>
								</TableHead>
							))}
						</TableRow>
					</TableHeader>
					<TableBody>
						{filteredData.map((row, rowIndex) => (
							<TableRow
								key={`row-${rowIndex}`}
								className="cursor-pointer"
								onClick={() => onRowClick?.(row)}
							>
								{columns.map((column, cellIndex) => (
									<TableCell key={`cell-${rowIndex}-${cellIndex}`}>
										{column.render
											? column.render(row[column.key], row)
											: row[column.key]}
									</TableCell>
								))}
							</TableRow>
						))}
					</TableBody>
				</Table>

				{enablePagination && totalPages > 0 && (
					<div className="flex items-center justify-between px-2 py-4">
						<div className="flex items-center space-x-2">
							<p className="text-sm font-medium">Αποτελέσματα ανά σελίδα</p>
							<Select
								value={String(itemsPerPage)}
								onValueChange={handleItemsPerPageChange}
							>
								<SelectTrigger className="h-8 w-[70px]">
									<SelectValue placeholder={itemsPerPage} />
								</SelectTrigger>
								<SelectContent side="top">
									{itemsPerPageOptions.map((pageSize) => (
										<SelectItem key={pageSize} value={String(pageSize)}>
											{pageSize}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							<p className="text-sm text-muted-foreground">
								Εμφάνιση {(currentPage - 1) * itemsPerPage + 1} έως{" "}
								{Math.min(currentPage * itemsPerPage, totalItems)} από{" "}
								{totalItems} αποτελέσματα
							</p>
						</div>

						<Pagination>
							<PaginationContent>
								<PaginationItem>
									<PaginationPrevious
										onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
										disabled={currentPage === 1}
									/>
								</PaginationItem>

								{getPageNumbers().map((page, index) => (
									<PaginationItem key={index}>
										{page === "ellipsis" ? (
											<PaginationEllipsis />
										) : (
											<PaginationLink
												onClick={() => setCurrentPage(Number(page))}
												isActive={currentPage === page}
											>
												{page}
											</PaginationLink>
										)}
									</PaginationItem>
								))}

								<PaginationItem>
									<PaginationNext
										onClick={() =>
											setCurrentPage(Math.min(totalPages, currentPage + 1))
										}
										disabled={currentPage === totalPages}
									/>
								</PaginationItem>
							</PaginationContent>
						</Pagination>
					</div>
				)}
			</CardContent>
		</Card>
	);
};

export default ReusableTable;
